import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Briefcase, Stethoscope, Lock, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/auth/AuthContext";
import { BusinessCard, CategoryChip } from "@/components/Cards";
import StoryRail from "@/components/StoryRail";

const Directory = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const categoryKeys = useMemo(() => Object.keys(t.categories), [t]);

  useEffect(() => {
    if (user === null) return; // wait for auth check so token is attached when logged in
    setLoading(true);
    const handle = setTimeout(() => {
      api.get("/entrepreneurs", { params: { q: q || undefined, category, type: "entrepreneur", limit: 60 } })
        .then(({ data }) => { setData(data); setError(""); })
        .catch((e) => setError(formatApiError(e)))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(handle);
  }, [q, category, user]);

  if (user === null) {
    return <div className="container-tight py-24 text-center text-teal-soft">…</div>;
  }

  return (
    <section className="section bg-cream min-h-[80vh]">
      <div className="container-tight">
        <div className="text-center max-w-3xl mx-auto">
          <p className="eyebrow text-orange">{t.nav.directory}</p>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-teal-deep mt-3 leading-tight break-words">{t.directory.title}</h1>
          <p className="mt-4 text-teal-soft text-lg">{t.directory.subtitle}</p>
        </div>

        {/* Visitor banner: browsing is open, contact data is for members */}
        {user === false && (
          <div className="mt-8 max-w-2xl mx-auto bg-teal-deep rounded-2xl px-5 py-4 flex items-center justify-between gap-4 flex-wrap" data-testid="directory-visitor-banner">
            <p className="text-white/90 text-sm inline-flex items-center gap-2">
              <Lock size={14} className="text-orange shrink-0" /> {t.community.dirVisitorBanner}
            </p>
            <Link to="/user" className="inline-flex items-center gap-2 bg-orange hover:bg-orange/90 text-white text-xs font-bold uppercase tracking-wider rounded-[10px] px-4 py-2.5 transition-colors shrink-0" data-testid="directory-visitor-join">
              {t.community.contactLockedCta} <ArrowRight size={12} />
            </Link>
          </div>
        )}

        {/* Subtitle hint: professionals now live in RED Online */}
        <div className="mt-6 text-center">
          <p className="text-sm text-teal-soft inline-flex items-center gap-2 flex-wrap justify-center">
            <Briefcase size={14} className="text-orange" />
            <span className="font-semibold text-teal-deep">{t.directory.entrepreneursAndBusiness}</span>
            <span className="text-gray-300">·</span>
            <a href="/red-apoyo" className="text-orange hover:underline inline-flex items-center gap-1">
              <Stethoscope size={14} /> {t.directory.lookingForPros}
            </a>
          </p>
        </div>

        <div className="mt-8 max-w-2xl mx-auto relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-teal-soft" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t.directory.searchPlaceholder}
            className="field-input !pl-11 py-4 rounded-full text-base"
            data-testid="directory-search"
          />
        </div>

        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <CategoryChip value="all" label={t.directory.all} active={category === "all"} onClick={setCategory} />
          {categoryKeys.map((k) => (
            <CategoryChip key={k} value={k} label={t.categories[k]} active={category === k} onClick={setCategory} />
          ))}
        </div>

        <div className="mt-6 text-center text-sm text-teal-soft" data-testid="directory-count">
          {data.total} {t.directory.results}
        </div>

        {error && <p className="text-center text-red-600 mt-6">{error}</p>}

        {/* Story rail — all active stories across the network */}
        <div className="mt-8" data-testid="directory-stories">
          <StoryRail mode="global" currentUserId={user?.id || null} />
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={`skeleton-${i}`} className="biz-card animate-pulse h-96 bg-gray-100" />
            ))
          ) : data.items.length ? (
            data.items.map((b) => <BusinessCard key={b.id} biz={b} />)
          ) : (
            <p className="col-span-full text-center text-teal-soft py-16">
              {t.directory.empty}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default Directory;
